import { Cancel } from '@mui/icons-material';
import { ReportProblem } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import { updateSGRWarrantyRequest } from 'lib/api/sgr';
import { startClaimProcess } from 'lib/api/sgr';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

interface Props {
  open: boolean;
  onClose: () => void;
  type: 'cancel' | 'claim';
  warrantyRequestId: number;
  refreshWarranty: () => void;
}

interface Values {
  icon: React.ReactNode;
  color: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  description: string;
}

const getValues = (type: 'cancel' | 'claim'): Values | null => {
  switch (type) {
    case 'cancel':
      return {
        icon: <Cancel color="error" />,
        color: 'error',
        title: 'Confirmar Anulación',
        message:
          '¿Estás seguro de querer anular esta garantía? Al anularla, el documento asociado ya no estará disponible para el cliente.',
        description: 'Esta acción no se puede deshacer una vez confirmada.',
      };
    case 'claim':
      return {
        icon: <ReportProblem color="warning" />,
        color: 'warning',
        title: 'Confirmar Siniestro',
        message:
          '¿Estás seguro de querer iniciar el proceso de siniestro para esta garantía? Al iniciar el proceso, el documento ya no estará disponible para el cliente y se iniciará un proceso de cobranza por el monto de la garantía.',
        description: 'Esta acción no se puede deshacer una vez confirmada.',
      };
    default:
      return null;
  }
};

const ConfirmActionDialog = ({
  open,
  onClose,
  type,
  warrantyRequestId,
  refreshWarranty,
}: Props) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [claimReason, setClaimReason] = useState('');
  const values = getValues(type);
  if (!values) return null;
  const { icon, color, title, message, description } = values;

  const handleConfirm = async () => {
    try {
      setLoading(true);
      if (type === 'claim') {
        if (!claimReason.trim()) {
          showAlert(dispatch, 'error', 'Debes ingresar un motivo de siniestro');
          return;
        }
        await startClaimProcess(dispatch, warrantyRequestId, { claimReason });
      } else {
        await updateSGRWarrantyRequest(dispatch, warrantyRequestId, {
          status: 'CANCELLED',
        });
      }
      if (refreshWarranty) refreshWarranty();
      showAlert(dispatch, 'success', 'Acción confirmada exitosamente');
    } catch (error) {
      errorHandler(dispatch, 'Problema al confirmar la acción');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle onClose={onClose} title={title} icon={icon} color={color} />
      <DialogContent>
        {type === 'claim' && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            margin="normal"
            label="Motivo del siniestro"
            placeholder="Describe el motivo del siniestro"
            value={claimReason}
            onChange={(e) => setClaimReason(e.target.value)}
          />
        )}
        <AlertBanner
          severity={color}
          title={title}
          message={message}
          description={description}
        />
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton color={color} onClick={handleConfirm} loading={loading}>
          Confirmar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmActionDialog;
